import Immutable from "immutable";
import React from "react";
import useFetchChoices from "../../../../common/choices/useFetchChoices";
import useCurrentParentInputs from "../../../../common/forms/useCurrentParentInputs";
import useFormComponentState from "../../../../common/forms/useFormComponentState";
import useMatchInputToChoices from "../../../../common/forms/useMatchInputToChoices";
import useVisibilityState from "../../../../common/forms/useVisibilityState";
import { getChoicesInInput } from "../../choices/choices";
import { getPrefixedChoiceLabel } from "../../utils";
import { SearchCriterion } from "../common/SearchCriterion";
import { SetFetchedChoicesInitializer } from "../SetFetchedChoicesInitializer";
import CheckboxesDropdown, { CheckboxesDropdownProps } from "./CheckboxesDropdown";
import { checkboxesChoicesType, requiredRolesType } from "../../../../common/types/search/search";
import { visibilitySelectorType } from "../../../../common/types/common";

export const STANDARD_CHOICE_CHECKBOX_CRITERION_INITIAL_STATE =
	Immutable.List();

interface StandardChoiceCheckboxCriterionProps extends CheckboxesDropdownProps {
	choicesMapper?: (choices: checkboxesChoicesType) => checkboxesChoicesType;
	criterionKey: string;
	endpoint: string;
	label: string;
	parentKeys?: string[];
	parentKeysMap?: { [key: string]: string } | null;
	prefix?: string;
	quickSearch?: boolean;
	requiredRoles?: requiredRolesType;
	visibilitySelector?: visibilitySelectorType;
}

export function StandardChoiceCheckboxCriterion({
	choicesMapper = (choices) => choices,
	criterionKey,
	endpoint,
	label,
	parentKeys = [],
	parentKeysMap = null,
	prefix,
	quickSearch = false,
	requiredRoles,
	visibilitySelector,
	...props
}: StandardChoiceCheckboxCriterionProps) {
	const visible = useVisibilityState(visibilitySelector);
	const { currentInput, onInputChange, onClearInput } = useFormComponentState(
		criterionKey,
		STANDARD_CHOICE_CHECKBOX_CRITERION_INITIAL_STATE,
	);
	const parentInputs = useCurrentParentInputs(parentKeys, parentKeysMap);
	const { choices, loading } = useFetchChoices(endpoint, parentInputs);
	useMatchInputToChoices(currentInput, choices, onInputChange);

	return (
		<SearchCriterion
			label={label}
			prefix={prefix}
			quickSearch={quickSearch}
			requiredRoles={requiredRoles}
			visibilitySelector={visibilitySelector}
		>
			<SetFetchedChoicesInitializer
				criterionKey={criterionKey}
				criterionLabel={getPrefixedChoiceLabel(label, prefix)}
				criterionChoices={getChoicesInInput(
					choicesMapper(choices),
					currentInput,
					visible,
				)}
			/>
			<CheckboxesDropdown
				{...props}
				label={label}
				loading={loading}
				choices={choicesMapper(choices) || []}
				criterionKey={criterionKey}
				currentInput={currentInput}
				onInputChange={onInputChange}
				onClearInput={onClearInput}
				prefix={prefix}
				quickSearch={quickSearch}
			/>
		</SearchCriterion>
	);
}
